import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Card, CardContent, CardHeader, CardTitle, CardDescription 
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";
import { 
  Send, RefreshCw, Eye, Clock, CheckCircle2, XCircle, 
  Users, Calendar, Mail, Settings, History, AlertCircle,
  ChevronLeft, ChevronRight, Loader2, Unlock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: "Janvier" },
  { value: 2, label: "Février" },
  { value: 3, label: "Mars" },
  { value: 4, label: "Avril" },
  { value: 5, label: "Mai" },
  { value: 6, label: "Juin" },
  { value: 7, label: "Juillet" },
  { value: 8, label: "Août" },
  { value: 9, label: "Septembre" },
  { value: 10, label: "Octobre" },
  { value: 11, label: "Novembre" },
  { value: 12, label: "Décembre" }
];

export default function PayrollVariablesPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingAll, setSendingAll] = useState(false);
  
  // Current period
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  
  // Data
  const [variables, setVariables] = useState([]);
  const [stats, setStats] = useState({ total: 0, draft: 0, sent: 0, excluded: 0 });
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  
  // Modals
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  
  // Editing state for inline edits
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");

  // Fetch monthly variables
  const fetchVariables = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/hr/payroll-variables/monthly/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setVariables(res.data.variables || []);
      setStats(res.data.stats || { total: 0, draft: 0, sent: 0, excluded: 0 });
    } catch (err) {
      toast.error("Erreur lors du chargement des variables");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth]);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/api/hr/payroll-variables/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data);
    } catch (err) {
      console.error("Error fetching settings:", err);
    }
  }, [token]);

  // Fetch history
  const fetchHistory = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(
        `${API}/api/hr/payroll-variables/history?year=${selectedYear}&month=${selectedMonth}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistory(res.data.submissions || []);
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  }, [token, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchVariables();
    fetchSettings();
  }, [fetchVariables, fetchSettings]);

  // Update a single field
  const updateField = async (variableId, field, value) => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/hr/payroll-variables/${variableId}`,
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local state
      setVariables(prev => prev.map(v => 
        v.id === variableId ? { ...v, [field]: value } : v
      ));
      toast.success("Sauvegardé");
    } catch (err) {
      toast.error("Erreur de sauvegarde");
      console.error(err);
    } finally {
      setSaving(false);
      setEditingCell(null);
    }
  };

  // Toggle include in auto send
  const toggleInclude = async (variableId, currentValue) => {
    const endpoint = currentValue 
      ? `${API}/api/hr/payroll-variables/${variableId}/exclude`
      : `${API}/api/hr/payroll-variables/${variableId}/include`;
    
    try {
      await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchVariables();
      toast.success(currentValue ? "Exclu de l'envoi" : "Inclus dans l'envoi");
    } catch (err) {
      toast.error("Erreur");
    }
  };

  // Send single email
  const sendSingle = async (variableId) => {
    try {
      await axios.post(
        `${API}/api/hr/payroll-variables/send/${variableId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email envoyé !");
      await fetchVariables();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'envoi");
    }
  };

  // Send all included
  const sendAll = async () => {
    setSendingAll(true);
    try {
      const res = await axios.post(
        `${API}/api/hr/payroll-variables/send-batch/${selectedYear}/${selectedMonth}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.success} email(s) envoyé(s), ${res.data.failed} échec(s)`);
      await fetchVariables();
    } catch (err) {
      toast.error("Erreur lors de l'envoi groupé");
    } finally {
      setSendingAll(false);
    }
  };

  // Reopen a sent variable for edit + resend
  const reopenVariable = async (variableId) => {
    if (!window.confirm("Rouvrir cet envoi ? La ligne repassera en brouillon : vous pourrez modifier les valeurs puis renvoyer au cabinet de paie.")) return;
    try {
      await axios.post(
        `${API}/api/hr/payroll-variables/${variableId}/reopen`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Ligne rouverte : modifiez puis renvoyez");
      await fetchVariables();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  // Preview email
  const previewEmail = async (variableId) => {
    try {
      const res = await axios.get(
        `${API}/api/hr/payroll-variables/preview/${variableId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPreviewData(res.data);
      setPreviewOpen(true);
    } catch (err) {
      toast.error("Erreur de prévisualisation");
    }
  };

  // Sync absences
  const syncAbsences = async () => {
    try {
      const res = await axios.post(
        `${API}/api/hr/payroll-variables/sync-absences/${selectedYear}/${selectedMonth}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${res.data.updated} enregistrements mis à jour`);
      await fetchVariables();
    } catch (err) {
      toast.error("Erreur de synchronisation");
    }
  };

  // Save settings
  const saveSettings = async (newSettings) => {
    try {
      await axios.put(
        `${API}/api/hr/payroll-variables/settings`,
        newSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Paramètres sauvegardés");
      setSettingsOpen(false);
      await fetchSettings();
    } catch (err) {
      toast.error("Erreur de sauvegarde");
    }
  };

  // Navigate months
  const prevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Status badge
  const StatusBadge = ({ status, sentAt }) => {
    if (status === "sent") {
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Envoyé {sentAt ? new Date(sentAt).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' }) : ""}
        </Badge>
      );
    }
    if (status === "excluded") {
      return (
        <Badge variant="outline" className="text-slate-500">
          <XCircle className="w-3 h-3 mr-1" />
          Exclu
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="text-orange-600 border-orange-300">
        <Clock className="w-3 h-3 mr-1" />
        À envoyer
      </Badge>
    );
  };

  // Editable cell component
  const EditableCell = ({ variable, field, type = "number", suffix = "" }) => {
    const isEditing = editingCell === `${variable.id}-${field}`;
    const value = variable[field];
    const isSent = variable.status === "sent";

    if (isSent) {
      return <span className="text-slate-600">{value ?? 0}{suffix}</span>;
    }

    if (isEditing) {
      return (
        <Input
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => {
            const newValue = type === "number" ? parseFloat(editValue) || 0 : editValue;
            if (newValue !== value) {
              updateField(variable.id, field, newValue);
            } else {
              setEditingCell(null);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const newValue = type === "number" ? parseFloat(editValue) || 0 : editValue;
              updateField(variable.id, field, newValue);
            }
            if (e.key === "Escape") {
              setEditingCell(null);
            }
          }}
          className="w-20 h-7 text-sm"
          autoFocus
        />
      );
    }

    return (
      <span
        className="cursor-pointer hover:bg-blue-50 px-2 py-1 rounded"
        onClick={() => {
          setEditingCell(`${variable.id}-${field}`);
          setEditValue(value ?? "");
        }}
      >
        {value ?? 0}{suffix}
      </span>
    );
  };

  const monthName = MONTHS.find(m => m.value === selectedMonth)?.label || "";

  return (
    <div className="p-6 space-y-6" data-testid="payroll-variables-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Variables de paie</h1>
          <p className="text-slate-600">
            Saisie des variables mensuelles pour le cabinet de paie
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchHistory(); setHistoryOpen(true); }}>
            <History className="w-4 h-4 mr-2" />
            Historique
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
            <Settings className="w-4 h-4 mr-2" />
            Paramètres
          </Button>
        </div>
      </div>

      {/* Period selector & Stats */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Period selector */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={prevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="flex items-center gap-2">
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="ghost" size="icon" onClick={nextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4 text-slate-400" />
                <span>{stats.total} salariés</span>
              </div>
              <Badge variant="outline" className="text-orange-600">
                {stats.draft} à envoyer
              </Badge>
              <Badge className="bg-green-100 text-green-800">
                {stats.sent} envoyés
              </Badge>
              {stats.excluded > 0 && (
                <Badge variant="secondary">
                  {stats.excluded} exclus
                </Badge>
              )}
            </div>

            {/* Auto send indicator */}
            {settings?.auto_send_enabled && (
              <div className="flex items-center gap-2 text-sm text-slate-600 bg-blue-50 px-3 py-1 rounded">
                <Calendar className="w-4 h-4 text-blue-500" />
                Envoi auto le {settings.auto_send_day_of_month}/{selectedMonth.toString().padStart(2, '0')}/{selectedYear}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={syncAbsences}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Resynchroniser absences
          </Button>
        </div>
        
        <Button 
          onClick={sendAll} 
          disabled={sendingAll || stats.draft === 0}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {sendingAll ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Envoyer tous les cochés ({stats.draft})
        </Button>
      </div>

      {/* Variables table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[200px]">Salarié</TableHead>
                    <TableHead className="text-center">H. Sup</TableHead>
                    <TableHead className="text-center">Absences</TableHead>
                    <TableHead className="text-center">CP</TableHead>
                    <TableHead className="text-center">Arrêts</TableHead>
                    <TableHead className="text-center">Avance</TableHead>
                    <TableHead className="text-center">Acompte</TableHead>
                    <TableHead className="text-center">Prime</TableHead>
                    <TableHead className="text-center">Tiers dét.</TableHead>
                    <TableHead>Commentaire</TableHead>
                    <TableHead className="text-center">Inclure</TableHead>
                    <TableHead className="text-center">Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {variables.map((v) => (
                    <TableRow 
                      key={v.id} 
                      className={v.status === "sent" ? "bg-green-50/50" : v.status === "excluded" ? "bg-slate-50" : ""}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">{v.employee_name}</div>
                          <div className="text-xs text-slate-500">{v.employee_matricule}</div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="overtime_hours" suffix="h" />
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="absences_days" suffix="j" />
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="paid_leave_days" suffix="j" />
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="sick_leave_days" suffix="j" />
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="advance_amount" suffix="€" />
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="acompte_amount" suffix="€" />
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="exceptional_bonus" suffix="€" />
                      </TableCell>
                      <TableCell className="text-center">
                        <EditableCell variable={v} field="tiers_detenteur_amount" suffix="€" />
                      </TableCell>
                      <TableCell className="max-w-[150px]">
                        {v.status !== "sent" ? (
                          <Input
                            value={v.comment || ""}
                            onChange={(e) => {
                              const newVal = e.target.value;
                              setVariables(prev => prev.map(x => 
                                x.id === v.id ? { ...x, comment: newVal } : x
                              ));
                            }}
                            onBlur={(e) => {
                              updateField(v.id, "comment", e.target.value);
                            }}
                            placeholder="..."
                            className="h-7 text-sm"
                            data-testid={`comment-input-${v.id}`}
                          />
                        ) : (
                          <span className="text-sm text-slate-600">{v.comment || "-"}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={v.include_in_auto_send}
                          onCheckedChange={() => toggleInclude(v.id, v.include_in_auto_send)}
                          disabled={v.status === "sent"}
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <StatusBadge status={v.status} sentAt={v.sent_at} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => previewEmail(v.id)}
                            title="Prévisualiser"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {v.status !== "sent" && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-blue-600 hover:text-blue-700"
                              onClick={() => sendSingle(v.id)}
                              title="Envoyer maintenant"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {v.status === "sent" && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-amber-600 hover:text-amber-700"
                              onClick={() => reopenVariable(v.id)}
                              title="Rouvrir pour modifier et renvoyer"
                              data-testid={`reopen-variable-btn-${v.id}`}
                            >
                              <Unlock className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Prévisualisation de l'email</DialogTitle>
          </DialogHeader>
          {previewData && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-500">Destinataire</label>
                <p className="text-slate-900">{previewData.recipient}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Objet</label>
                <p className="text-slate-900 font-medium">{previewData.subject}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-500">Corps du message</label>
                <pre className="mt-2 p-4 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap font-sans">
                  {previewData.body}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Paramètres des variables de paie</DialogTitle>
            <DialogDescription>
              Configuration de l'envoi des emails au cabinet de paie
            </DialogDescription>
          </DialogHeader>
          
          {settings && (
            <PayrollSettingsForm 
              settings={settings} 
              onSave={saveSettings}
              onCancel={() => setSettingsOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historique des envois</DialogTitle>
            <DialogDescription>
              {monthName} {selectedYear}
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Salarié</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Envoyé par</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((h) => (
                  <TableRow 
                    key={h.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setSelectedSubmission(h)}
                  >
                    <TableCell>
                      {new Date(h.sent_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}
                    </TableCell>
                    <TableCell>{h.employee_name}</TableCell>
                    <TableCell>
                      <Badge variant={h.sent_mode === "automatic" ? "secondary" : "outline"}>
                        {h.sent_mode === "automatic" ? "Auto" : "Manuel"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {h.status === "success" ? (
                        <Badge className="bg-green-100 text-green-800">Succès</Badge>
                      ) : (
                        <Badge variant="destructive">Échec</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {h.sent_by_name || "Système"}
                    </TableCell>
                  </TableRow>
                ))}
                {history.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                      Aucun envoi pour cette période
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submission Detail Modal */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détail de l'envoi</DialogTitle>
          </DialogHeader>
          {selectedSubmission && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-slate-500">Salarié</label>
                  <p className="font-medium">{selectedSubmission.employee_name}</p>
                </div>
                <div>
                  <label className="text-slate-500">Date d'envoi</label>
                  <p>{new Date(selectedSubmission.sent_at).toLocaleString('fr-FR', { timeZone: 'Europe/Paris' })}</p>
                </div>
                <div>
                  <label className="text-slate-500">Destinataire</label>
                  <p>{selectedSubmission.recipient_email}</p>
                </div>
                <div>
                  <label className="text-slate-500">Mode</label>
                  <p>{selectedSubmission.sent_mode === "automatic" ? "Automatique" : "Manuel"}</p>
                </div>
              </div>
              
              <div>
                <label className="text-sm text-slate-500">Objet</label>
                <p className="font-medium">{selectedSubmission.email_subject}</p>
              </div>
              
              <div>
                <label className="text-sm text-slate-500">Contenu de l'email</label>
                <pre className="mt-2 p-4 bg-slate-50 rounded-lg text-sm whitespace-pre-wrap font-sans max-h-64 overflow-y-auto">
                  {selectedSubmission.email_body}
                </pre>
              </div>
              
              {selectedSubmission.error_message && (
                <div className="bg-red-50 p-3 rounded-lg">
                  <label className="text-sm text-red-600 font-medium">Erreur</label>
                  <p className="text-red-700">{selectedSubmission.error_message}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSubmission(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Settings Form Component
function PayrollSettingsForm({ settings, onSave, onCancel }) {
  const [form, setForm] = useState({
    recipient_email: settings.recipient_email || "",
    recipient_first_name: settings.recipient_first_name || "",
    sender_email: settings.sender_email || "",
    bcc_emails: settings.bcc_emails?.join(", ") || "",
    signature: settings.signature || "",
    email_subject_template: settings.email_subject_template || "",
    email_body_template: settings.email_body_template || "",
    auto_send_enabled: settings.auto_send_enabled ?? true,
    auto_send_day_of_month: settings.auto_send_day_of_month || 22,
    auto_send_time: settings.auto_send_time || "09:00",
    reminder_j_minus_3: settings.reminder_j_minus_3 ?? true,
    reminder_j_minus_1: settings.reminder_j_minus_1 ?? true,
    send_summary_after: settings.send_summary_after ?? true
  });

  const handleSave = () => {
    const bccArray = form.bcc_emails
      .split(",")
      .map(e => e.trim())
      .filter(e => e.length > 0);
    
    onSave({
      ...form,
      bcc_emails: bccArray
    });
  };

  return (
    <Tabs defaultValue="recipient" className="mt-4">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="recipient">Destinataire</TabsTrigger>
        <TabsTrigger value="template">Template</TabsTrigger>
        <TabsTrigger value="auto">Envoi auto</TabsTrigger>
      </TabsList>
      
      <TabsContent value="recipient" className="space-y-4 mt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Email du cabinet de paie *</label>
            <Input
              type="email"
              value={form.recipient_email}
              onChange={(e) => setForm({ ...form, recipient_email: e.target.value })}
              placeholder="comptable@cabinet.fr"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Prénom du contact</label>
            <Input
              value={form.recipient_first_name}
              onChange={(e) => setForm({ ...form, recipient_first_name: e.target.value })}
              placeholder="CELIA"
            />
          </div>
        </div>
        
        <div>
          <label className="text-sm font-medium">Email expéditeur</label>
          <Input
            type="email"
            value={form.sender_email}
            onChange={(e) => setForm({ ...form, sender_email: e.target.value })}
            placeholder="rh@betaye.fr"
          />
        </div>
        
        <div>
          <label className="text-sm font-medium">Copie cachée (BCC)</label>
          <Input
            value={form.bcc_emails}
            onChange={(e) => setForm({ ...form, bcc_emails: e.target.value })}
            placeholder="admin@betaye.fr, direction@betaye.fr"
          />
          <p className="text-xs text-slate-500 mt-1">Séparez les emails par des virgules</p>
        </div>
        
        <div>
          <label className="text-sm font-medium">Signature</label>
          <Textarea
            value={form.signature}
            onChange={(e) => setForm({ ...form, signature: e.target.value })}
            rows={3}
          />
        </div>
      </TabsContent>
      
      <TabsContent value="template" className="space-y-4 mt-4">
        <div>
          <label className="text-sm font-medium">Objet de l'email</label>
          <Input
            value={form.email_subject_template}
            onChange={(e) => setForm({ ...form, email_subject_template: e.target.value })}
          />
          <p className="text-xs text-slate-500 mt-1">
            Variables: {"{MOIS}"}, {"{ANNEE}"}, {"{NOM}"}, {"{PRENOM}"}, {"{MATRICULE}"}
          </p>
        </div>
        
        <div>
          <label className="text-sm font-medium">Corps de l'email</label>
          <Textarea
            value={form.email_body_template}
            onChange={(e) => setForm({ ...form, email_body_template: e.target.value })}
            rows={15}
            className="font-mono text-sm"
          />
          <p className="text-xs text-slate-500 mt-1">
            Variables disponibles: {"{PRENOM_DESTINATAIRE}"}, {"{CIVILITE}"}, {"{NOM_SALARIE}"}, 
            {"{MOIS}"}, {"{ANNEE}"}, {"{HEURES_SUP}"}, {"{ABSENCES}"}, {"{CONGES_PAYES}"}, 
            {"{ARRETS_TRAVAIL}"}, {"{AVANCE}"}, {"{ACOMPTE}"}, {"{PRIME}"}, {"{TIERS_DETENTEUR}"}, {"{COMMENTAIRE}"}, {"{SIGNATURE}"}
          </p>
        </div>
      </TabsContent>
      
      <TabsContent value="auto" className="space-y-4 mt-4">
        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
          <div>
            <p className="font-medium">Activer l'envoi automatique</p>
            <p className="text-sm text-slate-500">
              Envoyer automatiquement les variables chaque mois
            </p>
          </div>
          <Checkbox
            checked={form.auto_send_enabled}
            onCheckedChange={(checked) => setForm({ ...form, auto_send_enabled: checked })}
          />
        </div>
        
        {form.auto_send_enabled && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Jour du mois</label>
                <Select 
                  value={form.auto_send_day_of_month.toString()} 
                  onValueChange={(v) => setForm({ ...form, auto_send_day_of_month: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 28 }, (_, i) => i + 1).map(d => (
                      <SelectItem key={d} value={d.toString()}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Heure d'envoi</label>
                <Input
                  type="time"
                  value={form.auto_send_time}
                  onChange={(e) => setForm({ ...form, auto_send_time: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="reminder3"
                  checked={form.reminder_j_minus_3}
                  onCheckedChange={(checked) => setForm({ ...form, reminder_j_minus_3: checked })}
                />
                <label htmlFor="reminder3" className="text-sm">
                  Envoyer un rappel J-3 avant l'envoi
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="reminder1"
                  checked={form.reminder_j_minus_1}
                  onCheckedChange={(checked) => setForm({ ...form, reminder_j_minus_1: checked })}
                />
                <label htmlFor="reminder1" className="text-sm">
                  Envoyer un rappel J-1 avant l'envoi
                </label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="summary"
                  checked={form.send_summary_after}
                  onCheckedChange={(checked) => setForm({ ...form, send_summary_after: checked })}
                />
                <label htmlFor="summary" className="text-sm">
                  Envoyer un récapitulatif après l'envoi automatique
                </label>
              </div>
            </div>
          </>
        )}
      </TabsContent>
      
      <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button onClick={handleSave}>
          Enregistrer
        </Button>
      </div>
    </Tabs>
  );
}
